<template>
  <div class="blog-main-content">
    <BlogList class="right"/>
    <h1>Lightning + Chain Lightning Sorceress Build Guide</h1>
    <p>The Lightning Sorceress is one of the most powerful ranged builds in Diablo 2: Resurrected. It utilizes <strong>Lightning</strong> and <strong>Chain Lightning</strong> to deal massive AoE damage. With a Mercenary wielding <strong>Infinity</strong>, enemy lightning resistances are significantly reduced, allowing this build to excel even against high-resistance foes.</p>

    <div>
      <img class="build-screenshot" alt="build screenshot" src="@/assets/builds/bd-lightning-sor.png">
    </div>
    
    <div class="section">
        <h2>Recommended Gear</h2>
        <Equipments
          class="right"
          :left-weapon="weapon"
          :right-weapon="shield"
          :helm="helm"
          :armor="armor"
          :gloves="gloves"
          :belt="belt"
        />
        <h3>Weapon</h3>
        <ul>
            <li>Crescent Moon – Lowers enemy lightning resistance</li>
            <li>Heart of the Oak – +3 Skills, High Resistances</li>
            <li>Spirit – FCR, mana regen, +2 skills</li>
        </ul>
        <h3>Shield</h3>
        <ul>
            <li>Spirit Monarch – +2 skills, FCR, resistances, and mana boost</li>
        </ul>
        <h3>Helmet</h3>
        <ul>
            <li>Griffon's Eye – Best option for lightning damage</li>
            <li>Harlequin Crest (Shako) – High survivability and +2 skills</li>
        </ul>
        <h3>Armor</h3>
        <ul>
            <li>Skin of the Vipermagi – FCR, resistances, budget option</li>
        </ul>
        <h3>Belt</h3>
        <ul>
            <li>Arachnid Mesh – FCR, +1 skills</li>
        </ul>
        <h3>Gloves</h3>
        <ul>
            <li>Trang-Oul's Claws – FCR, cold resistance</li>
            <li>Magefist – FCR, mana regeneration</li>
        </ul>
        <h3>Rings</h3>
        <ul>
            <li>Stone of Jordan – +1 skills, mana increase</li>
            <li>If FCR is below 117%, replace with a rare/crafted ring with 10% FCR</li>
        </ul>
        <h3>Amulet</h3>
        <ul>
            <li>Mara's Kaleidoscope – +2 skills, resistances</li>
            <li>A +3 Lightning Skills amulet or +2 Sorceress Skills crafted amulet</li>
        </ul>

        <h3>Inventory</h3>
        <ul>
            <li><strong>Crack of the Heavens</strong> – Used to break lightning immunities of monsters.</li>
        </ul>
    </div>

    <div id="slot-content_mpu" class="content-ad-mpu"/>

    <div class="section">
        <h2>Skill Allocation</h2>
        <div class="m-3 skills-tab right">
          <b-tabs small content-class="m-2" class="tab-text">
            <b-tab title="Cold">
              <img class="w-100" src="@/assets/builds/skills-lightning-sor-1.png">
            </b-tab>
            <b-tab title="Lightning" active>
              <img class="w-100" src="@/assets/builds/skills-lightning-sor-2.png">
            </b-tab>
            <b-tab title="Fire">
              <img class="w-100" src="@/assets/builds/skills-lightning-sor-3.png">
            </b-tab>
          </b-tabs>
        </div>
        <h3>Core Skills (Max 20 Points Each)</h3>
        <ul>
            <li>Lightning – Main single-target damage skill</li>
            <li>Chain Lightning – Best for clearing groups of enemies</li>
            <li>Lightning Mastery – Increases all lightning skill damage</li>
            <li>Charged Bolt – Synergy for Lightning</li>
        </ul>
        <h3>Damage-increasing Skills (add as many as possible)</h3>
        <ul>
          <li>Nova – Synergy for Lightning</li>
        </ul>
        <h3>Support Skills (1 Point Each)</h3>
        <ul>
            <li>Static Field – Reduces enemy HP quickly</li>
            <li>Teleport – Essential for mobility</li>
            <li>Frozen Armor – Increases defense and survivability</li>
            <li>Warmth – Boosts mana regeneration</li>
        </ul>
    </div>
    
    <div class="section">
        <h2>Stat Distribution</h2>
        <ul>
            <li><strong>Strength:</strong> Just enough to equip gear</li>
            <li><strong>Dexterity:</strong> No points (unless aiming for max block)</li>
            <li><strong>Vitality:</strong> All remaining points</li>
            <li><strong>Energy:</strong> Few points (If get enough Mana from gear, buffs, Charms, no need to allocate any points into Energy)</li>
        </ul>
    </div>
    
    <div class="section">
        <h2>Mercenary Choice</h2>
        <p>Recommended: Act 2 Nightmare/Hell <strong>Holy Freeze</strong> Mercenary</p>
        <h3>Gear:</h3>
        <ul>
            <li><strong>Weapon:</strong> Infinity – Lowers enemy lightning resistance</li>
            <li><strong>Helmet:</strong> Andariel's Visage – Increased attack speed and life leech</li>
            <li><strong>Armor:</strong> Fortitude – Increased damage and survivability</li>
        </ul>
    </div>
    
    <div class="section">
        <h2>Gameplay Tips</h2>
        <ul>
            <li>Use <strong>Static Field</strong> to weaken enemies before attacking</li>
            <li>Use <strong>Chain Lightning</strong> for groups of enemies</li>
            <li>Use <strong>Lightning</strong> for single-target damage</li>
            <li>Maintain <strong>Teleport</strong> for mobility and survivability</li>
        </ul>
    </div>
    
    <p>The Lightning + Chain Lightning Sorceress is an exceptional build with high damage and mobility. Using <strong>Crescent Moon or Spirit</strong> for the player and <strong>Infinity</strong> on the mercenary ensures optimal damage output and efficient farming.</p>
  </div>
</template>

<script>
import BlogList from '../BlogList.vue'
import Equipments from './Equipments.vue'

export default {
  name: 'LightningSor',

  components: {
    BlogList,
    Equipments,
  },

  computed: {
    weapon () {
      return {
        'img': 'eq-crystal-sword',
        'width': 80,
        'title': 'Crescent Moon Runeword',
        'runes': 'Shael + Um + Tir',
        'detail': [
          '10% Chance To Cast Level 17 Chain Lightning On Striking',
          '7% Chance To Cast Level 13 Static Field On Striking',
          '+20% Increased Attack Speed',
          '+180-220% Enhanced Damage',
          "Ignore Target's Defense",
          '-35% To Enemy Lightning Resistance',
          '25% Chance of Open Wounds',
          '+9-11 Magic Absorb',
          '+2 To Mana After Each Kill',
          'Level 18 Summon Spirit Wolf (30 Charges)',
        ],
      }
    },

    shield () {
      return {
        'img': 'eq-monarch',
        'width': 80,
        'title': 'Spirit Runeword',
        'runes': 'Tal + Thul + Ort + Amn',
        'detail': [
          '+2 To All Skills', 
          '+25-35% Faster Cast Rate',
          '+55% Faster Hit Recovery',
          '+250 Defense Vs. Missile', 
          '+22 To Vitality',
          '+89-112 To Mana',
          'Cold Resist +35%',
          'Lightning Resist +35%',
          'Poison Resist +35%',
          '+3-8 Magic Absorb',
          'Attacker Takes Damage of 14',
        ],
      }
    },

    helm () {
      return {
        'img': 'eq-diadem',
        'width': 75,
        'title': "Griffon's Eye",
        'detail': [
          'Defense: 150-260', 
          '+100-200 Defense',
          '+1 To All Skills',
          '+25% Faster Cast Rate', 
          '-15-20% To Enemy Lightning Resistance',
          '+10-15% To Lightning Skill Damage'
        ],
      }
    },

    armor () {
      return {
        'img': 'eq-skin-of-viper',
        'width': 75,
        'title': 'Skin of the Vipermagi',
        'detail': [
          'Defense: 279',
          '+120% Enhanced Defense',
          '+1 To All Skills', 
          '30% Faster Cast Rate',
          'Magic Damage Reduced By 9-13',
          'All Resistances +20-35', 
        ],
      }
    },

    gloves () {
      return {
        'img': 'eq-magefist',
        'width': 70,
        'title': 'Magefist',
        'detail': [
          'Defense: 24-25', 
          '+20-30% Enhanced Defense',
          '+10 Defense',
          '+1 To Fire Skills', 
          '+20% Faster Cast Rate',
          '+30% Faster Hit Recovery',
          'Regenerate Mana 25%',
          'Adds 1-6 Fire Damage',
        ],
      }
    },

    belt () {
      return {
        'img': 'eq-arachnid-mesh',
        'width': 75,
        'title': 'Arachnid Mesh',
        'detail': [
          'Defense: 119-138', 
          '+90-120% Enhanced Defense',
          'Slows Target By 10%',
          '+1 To All Skills', 
          '+20% Faster Cast Rate',
          'Increases Maximum Mana 5%',
          'Level 3 Venom (11 Charges)',
        ],
      }
    },
  }
}
</script>
